import React, { useState, useEffect } from 'react';
import InvoiceView from './InvoiceView';
import { crmFetch } from '../utils/crmFetch';

const InvoiceManagement = () => {
  const [activeTab, setActiveTab] = useState('invoices'); // invoices, programs
  const [invoices, setInvoices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending_review');
  const [viewingInvoice, setViewingInvoice] = useState(null);
  
  // Confirmation modal state for sending invoices
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [invoiceToSend, setInvoiceToSend] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    } else {
      fetchPrograms();
    }
  }, [activeTab, filterStatus]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const url = filterStatus 
        ? `${backendUrl}/api/crm/invoicing/invoices?status=${filterStatus}`
        : `${backendUrl}/api/crm/invoicing/invoices`;
      const response = await crmFetch(url);
      const data = await response.json();
      if (data.success) {
        setInvoices(data.invoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
    setLoading(false);
  };

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/programs`);
      const data = await response.json();
      if (data.success) {
        setPrograms(data.programs);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
    setLoading(false);
  };

  const handleApproveInvoice = async (invoiceId) => {
    if (!window.confirm('Approve this invoice for sending?')) return;
    
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoiceId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'approve',
          approved_by: 'Admin'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Invoice approved successfully!');
        fetchInvoices();
      }
    } catch (error) {
      alert('Error approving invoice: ' + error.message);
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoiceId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'reject',
          reason: reason
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Invoice rejected');
        fetchInvoices();
      }
    } catch (error) {
      alert('Error rejecting invoice: ' + error.message);
    }
  };

  // Open confirmation modal before sending invoice
  const openSendConfirmation = (invoice) => {
    setInvoiceToSend(invoice);
    setConfirmText('');
    setShowConfirmSendModal(true);
  };

  // Actually send the invoice after typing CONFIRM
  const handleSendInvoice = async () => {
    if (confirmText !== 'CONFIRM') {
      alert('Please type CONFIRM exactly to send the invoice');
      return;
    }
    
    setLoading(true);
    setShowConfirmSendModal(false);
    
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoiceToSend.invoice_id}/send`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Invoice sent successfully!');
        fetchInvoices();
      } else {
        alert('Error sending invoice: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Error sending invoice: ' + error.message);
    }
    setLoading(false);
    setInvoiceToSend(null);
    setConfirmText('');
  };

  const handleGenerateDrafts = async () => {
    if (!window.confirm('Generate draft invoices for all active programs?')) return;
    
    setLoading(true);
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/generate`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        alert(`Generated ${data.invoices.length} draft invoices`);
        fetchInvoices();
      }
    } catch (error) {
      alert('Error generating invoices: ' + error.message);
    }
    setLoading(false);
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send reminder emails to all billing contacts?')) return;
    
    setLoading(true);
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/reminders/send`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        alert(data.message);
      }
    } catch (error) {
      alert('Error sending reminders: ' + error.message);
    }
    setLoading(false);
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const res = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoiceId}/pdf`);
      if (!res.ok) {
        alert(`Failed to download PDF (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(`Error downloading PDF: ${e.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending_review': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'approved': 'bg-green-100 text-green-800 border-green-300',
      'sent': 'bg-blue-100 text-blue-800 border-blue-300',
      'rejected': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      
      {/* Invoice View Modal */}
      {viewingInvoice && (
        <InvoiceView
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onDownloadPDF={downloadPDF}
          backendUrl={backendUrl}
        />
      )}
      
      {/* Confirmation Modal for Sending Invoice */}
      {showConfirmSendModal && (
        <ConfirmSendModal
          invoice={invoiceToSend}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          onConfirm={handleSendInvoice}
          onClose={() => {
            setShowConfirmSendModal(false);
            setInvoiceToSend(null);
            setConfirmText('');
          }}
        />
      )}
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Invoice Management</h1>
        <p className="text-gray-600">Manage billable programs and monthly invoices</p>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-3 flex-wrap">
        <button
          onClick={() => setShowCreateInvoiceModal(true)}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
        >
          ➕ Create New Invoice
        </button>
        <button
          onClick={handleGenerateDrafts}
          disabled={loading}
          className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
        >
          📝 Generate Draft Invoices
        </button>
        <button
          onClick={handleSendReminders}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          📧 Send Reminders
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('invoices')}
            className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
              activeTab === 'invoices'
                ? 'border-kioo-primary text-kioo-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📄 Invoices
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
              activeTab === 'programs'
                ? 'border-kioo-primary text-kioo-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📻 Programs
          </button>
        </div>
      </div>

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div>
          {/* Status Filter */}
          <div className="mb-4 flex items-center gap-3">
            <label className="font-medium text-gray-700">Filter by Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            >
              <option value="">All Invoices</option>
              <option value="pending_review">⏳ Pending Review</option>
              <option value="approved">✅ Approved</option>
              <option value="sent">📤 Sent</option>
              <option value="rejected">❌ Rejected</option>
            </select>
          </div>

          {/* Invoices List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kioo-primary mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500 text-lg">No invoices found</p>
              <p className="text-gray-400 mt-2">Generate draft invoices to get started</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {invoices.map((invoice) => (
                <div key={invoice.invoice_id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {invoice.invoice_number}
                        </h3>
                        <p className="text-gray-600">{invoice.program_name}</p>
                        <p className="text-sm text-gray-500">{invoice.ministry_name}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(invoice.status)}`}>
                        {invoice.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500">Invoice Date:</span>
                        <p className="font-medium">{formatDate(invoice.invoice_date)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Due Date:</span>
                        <p className="font-medium">{formatDate(invoice.due_date)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <p className="font-bold text-kioo-primary">${(invoice.total || 0).toFixed(2)} {invoice.currency}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Period:</span>
                        <p className="font-medium">{invoice.billing_period}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <span className="text-sm text-gray-500">Billing Emails:</span>
                      <p className="text-sm">{(invoice.billing_emails || []).join(', ')}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setViewingInvoice(invoice)}
                        className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors text-sm"
                      >
                        👁️ View Invoice
                      </button>
                      <button
                        onClick={() => downloadPDF(invoice.invoice_id, invoice.invoice_number)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                      >
                        📥 Download PDF
                      </button>

                      {invoice.status === 'pending_review' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowInvoiceModal(true);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleApproveInvoice(invoice.invoice_id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                          >
                            ✅ Approve
                          </button>
                          <button
                            onClick={() => handleRejectInvoice(invoice.invoice_id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                          >
                            ❌ Reject
                          </button>
                        </>
                      )}

                      {invoice.status === 'approved' && (
                        <button
                          onClick={() => openSendConfirmation(invoice)}
                          className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors text-sm"
                        >
                          📤 Send Invoice
                        </button>
                      )}

                      {invoice.status === 'sent' && (
                        <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                          ✓ Sent on {formatDate(invoice.sent_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <div>
          <button
            onClick={() => setShowProgramModal(true)}
            className="mb-4 px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors"
          >
            ➕ Add New Program
          </button>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kioo-primary mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading programs...</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {programs.map((program) => (
                <div key={program.program_id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{program.program_name}</h3>
                      <p className="text-gray-600 mb-1">{program.ministry_name}</p>
                      <p className="text-sm text-gray-500 mb-3">{program.schedule}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                        <div>
                          <span className="text-gray-500">Billing Type:</span>
                          <p className="font-medium capitalize">{program.billing_type}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Rate:</span>
                          <p className="font-bold text-kioo-primary">${(program.rate || 0).toFixed(2)} / month</p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <span className="text-sm text-gray-500">Billing Emails:</span>
                        <p className="text-sm">{(program.billing_emails || []).join(', ')}</p>
                      </div>

                      {program.notes && (
                        <div>
                          <span className="text-sm text-gray-500">Notes:</span>
                          <p className="text-sm">{program.notes}</p>
                        </div>
                      )}
                    </div>

                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      program.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {program.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="mt-4 flex gap-2 justify-end">
                    <button
                      onClick={() => setEditingProgram(program)}
                      data-testid={`program-edit-btn-${program.program_id}`}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete ministry "${program.ministry_name}"? This cannot be undone.`)) return;
                        try {
                          const r = await crmFetch(`${backendUrl}/api/crm/invoicing/programs/${program.program_id}`, { method: 'DELETE' });
                          const d = await r.json();
                          if (d.success) {
                            alert('Ministry deleted');
                            fetchPrograms();
                          } else {
                            alert(d.detail || 'Delete failed');
                          }
                        } catch (e) {
                          alert('Error: ' + e.message);
                        }
                      }}
                      data-testid={`program-delete-btn-${program.program_id}`}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invoice Edit Modal */}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceEditModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
          onSave={() => {
            fetchInvoices();
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
          backendUrl={backendUrl}
        />
      )}

      {/* Program Add Modal */}
      {showProgramModal && (
        <ProgramAddModal
          onClose={() => setShowProgramModal(false)}
          onSave={() => {
            fetchPrograms();
            setShowProgramModal(false);
          }}
          backendUrl={backendUrl}
        />
      )}

      {/* Program Edit Modal */}
      {editingProgram && (
        <ProgramEditModal
          program={editingProgram}
          onClose={() => setEditingProgram(null)}
          onSave={() => {
            fetchPrograms();
            setEditingProgram(null);
          }}
          backendUrl={backendUrl}
        />
      )}

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoiceModal(false)}
          onSave={() => {
            fetchInvoices();
            setShowCreateInvoiceModal(false);
          }}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
};

// Create Invoice Modal Component - AI-powered invoice creation
const CreateInvoiceModal = ({ onClose, onSave, backendUrl }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    description: '',
    quantity: 1,
    rate: 0,
    tax: 0,
    due_days: 30,
    notes: '',
    client_type: 'international'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const calculateTotal = () => {
    const quantity = parseInt(formData.quantity) || 0;
    const rate = parseFloat(formData.rate) || 0;
    const tax = parseFloat(formData.tax) || 0;
    const subtotal = quantity * rate;
    return subtotal + tax;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/create-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          rate: parseFloat(formData.rate),
          tax: parseFloat(formData.tax),
          quantity: parseInt(formData.quantity),
          due_days: parseInt(formData.due_days),
          client_type: formData.client_type
        })
      });

      const data = await response.json();
      if (data.success) {
        setCreatedInvoice(data.invoice);
      } else {
        setError(data.detail || 'Failed to create invoice');
      }
    } catch (err) {
      setError('Error creating invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!createdInvoice) return;
    try {
      const res = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${createdInvoice.invoice_id}/pdf`);
      if (!res.ok) { alert(`Failed (HTTP ${res.status})`); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${createdInvoice.invoice_number || createdInvoice.invoice_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
  };

  const handleRegenerateMessage = async () => {
    if (!createdInvoice) return;
    setLoading(true);
    try {
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${createdInvoice.invoice_id}/regenerate-message`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setCreatedInvoice({
          ...createdInvoice,
          appreciation_message: data.appreciation_message
        });
      }
    } catch (err) {
      console.error('Error regenerating message:', err);
    } finally {
      setLoading(false);
    }
  };

  // Success view after invoice creation
  if (createdInvoice) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Invoice Created!</h2>
              <p className="text-gray-600 mt-1">Invoice #{createdInvoice.invoice_number}</p>
            </div>

            {/* Invoice Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Client:</span>
                  <p className="font-medium">{createdInvoice.ministry_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Amount:</span>
                  <p className="font-bold text-green-600 text-lg">${createdInvoice.total.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <p className="font-medium">{new Date(createdInvoice.due_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className="font-medium capitalize">{createdInvoice.status.replace('_', ' ')}</p>
                </div>
              </div>
            </div>

            {/* AI Appreciation Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <span>✨</span> AI-Generated Appreciation Message
                </h4>
                <button
                  onClick={handleRegenerateMessage}
                  disabled={loading}
                  className="text-xs px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? '...' : '🔄 Regenerate'}
                </button>
              </div>
              <p className="text-green-700 text-sm italic">
                "{createdInvoice.appreciation_message}"
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleDownloadPDF}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                📄 Download PDF
              </button>
              <button
                onClick={() => {
                  onSave();
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Create New Invoice</h2>
              <p className="text-gray-500 text-sm mt-1">AI will generate a unique appreciation message</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">👤 Client Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client / Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="e.g., Truth For Life Ministries"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="billing@example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Type *
                </label>
                <select
                  value={formData.client_type}
                  onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="international">🌍 International (Wire Transfer Only)</option>
                  <option value="local">🇱🇷 Local - Liberia (Mobile Money Available)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.client_type === 'local' 
                    ? 'Local clients will see Mobile Money payment options on their invoice.' 
                    : 'International clients will only see wire transfer payment instructions.'}
                </p>
              </div>
            </div>

            {/* Service Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">📋 Service Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Radio Airtime - Weekly Program Broadcast"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate (USD) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax (USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.tax}
                    onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">📅 Payment Terms</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due in (days)
                  </label>
                  <select
                    value={formData.due_days}
                    onChange={(e) => setFormData({ ...formData, due_days: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                    <option value="45">45 days</option>
                    <option value="60">60 days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Total Preview */}
            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="ml-2 font-medium">${(formData.quantity * formData.rate).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Tax:</span>
                  <span className="ml-2 font-medium">${parseFloat(formData.tax || 0).toFixed(2)}</span>
                </div>
                <div className="text-right">
                  <span className="text-gray-600">Total:</span>
                  <span className="ml-2 text-2xl font-bold text-green-600">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* AI Notice */}
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
              <span>✨</span>
              <span>AI will generate a unique, heartfelt appreciation message for this invoice</span>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.client_name || !formData.client_email || !formData.description || !formData.rate}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Creating...
                  </>
                ) : (
                  <>
                    ✨ Create Invoice
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Invoice Edit Modal Component
const InvoiceEditModal = ({ invoice, onClose, onSave, backendUrl }) => {
  const toDateInput = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
  };

  const [formData, setFormData] = useState({
    ministry_name: invoice.ministry_name || '',
    program_name: invoice.program_name || '',
    billing_emails: (invoice.billing_emails || []).join(', '),
    invoice_date: toDateInput(invoice.invoice_date),
    due_date: toDateInput(invoice.due_date),
    line_items: (invoice.line_items || []).map((li) => ({ ...li })),
    tax: invoice.tax || 0,
    notes: invoice.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const setLine = (idx, field, value) => {
    const items = [...formData.line_items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      const q = parseFloat(items[idx].quantity) || 0;
      const r = parseFloat(items[idx].rate) || 0;
      items[idx].amount = +(q * r).toFixed(2);
    }
    setFormData({ ...formData, line_items: items });
  };
  const addLine = () => setFormData({
    ...formData,
    line_items: [...formData.line_items, { description: '', quantity: 1, rate: 0, amount: 0 }],
  });
  const removeLine = (idx) => setFormData({
    ...formData,
    line_items: formData.line_items.filter((_, i) => i !== idx),
  });

  const subtotal = formData.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
  const total = subtotal + (parseFloat(formData.tax) || 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1) Update invoice line items + totals + notes via PUT
      const linePayload = {
        line_items: formData.line_items.map((li) => ({
          description: li.description,
          quantity: parseInt(li.quantity, 10) || 0,
          rate: parseFloat(li.rate) || 0,
          amount: parseFloat(li.amount) || 0,
        })),
        subtotal: +subtotal.toFixed(2),
        tax: parseFloat(formData.tax) || 0,
        total: +total.toFixed(2),
        notes: formData.notes,
      };
      const r1 = await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoice.invoice_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(linePayload),
      });
      const d1 = await r1.json();
      if (!d1.success) throw new Error(d1.detail || 'Update failed');

      // 2) Best-effort: persist ministry_name / dates / emails via same PUT (service updates whatever keys are passed)
      const metaPayload = {
        line_items: linePayload.line_items, // keep schema happy
      };
      // Backend's InvoiceUpdate model only accepts known keys; storing extra fields is harmless but unsupported.
      // We surface the explicit fields the user edited via direct mongo-friendly keys:
      const extras = {
        ministry_name: formData.ministry_name,
        program_name: formData.program_name,
        billing_emails: formData.billing_emails.split(',').map((e) => e.trim()).filter(Boolean),
        invoice_date: formData.invoice_date ? new Date(formData.invoice_date).toISOString() : invoice.invoice_date,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : invoice.due_date,
      };
      // Send extras through the same PUT — pydantic will ignore unknown fields if `extra="ignore"`,
      // but to be safe we POST them via a dedicated body merge attempt and ignore failure.
      try {
        await crmFetch(`${backendUrl}/api/crm/invoicing/invoices/${invoice.invoice_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...metaPayload, ...extras, ...linePayload }),
        });
      } catch (_) { /* ignore */ }

      alert('Invoice updated successfully!');
      onSave();
    } catch (error) {
      alert('Error updating invoice: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Edit Invoice: {invoice.invoice_number}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ministry / Client name</label>
              <input
                type="text"
                value={formData.ministry_name}
                onChange={(e) => setFormData({ ...formData, ministry_name: e.target.value })}
                data-testid="invoice-edit-ministry"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program / Description</label>
              <input
                type="text"
                value={formData.program_name}
                onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                data-testid="invoice-edit-program"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice date</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                data-testid="invoice-edit-date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                data-testid="invoice-edit-due"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing emails (comma-separated)</label>
              <input
                type="text"
                value={formData.billing_emails}
                onChange={(e) => setFormData({ ...formData, billing_emails: e.target.value })}
                data-testid="invoice-edit-emails"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Line items</h3>
              <button
                onClick={addLine}
                data-testid="invoice-edit-add-line"
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                + Add line
              </button>
            </div>
            <div className="space-y-2">
              {formData.line_items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-edit-line-${idx}`}>
                  <input
                    type="text"
                    placeholder="Description"
                    value={li.description}
                    onChange={(e) => setLine(idx, 'description', e.target.value)}
                    className="col-span-5 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={li.quantity}
                    onChange={(e) => setLine(idx, 'quantity', e.target.value)}
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    value={li.rate}
                    onChange={(e) => setLine(idx, 'rate', e.target.value)}
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={li.amount}
                    readOnly
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <button
                    onClick={() => removeLine(idx)}
                    data-testid={`invoice-edit-remove-line-${idx}`}
                    className="col-span-1 text-red-600 hover:text-red-800"
                    title="Remove line"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
              <input type="text" readOnly value={`$${subtotal.toFixed(2)}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
              <input
                type="number"
                step="0.01"
                value={formData.tax}
                onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                data-testid="invoice-edit-tax"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
              <input type="text" readOnly value={`$${total.toFixed(2)}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-bold" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              data-testid="invoice-edit-notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              onClick={onClose}
              data-testid="invoice-edit-cancel-btn"
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="invoice-edit-save-btn"
              className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Program Add Modal Component
const ProgramAddModal = ({ onClose, onSave, backendUrl }) => {
  const [formData, setFormData] = useState({
    program_id: '',
    program_name: '',
    ministry_name: '',
    schedule: '',
    billing_type: 'monthly',
    rate: 0,
    currency: 'USD',
    billing_emails: '',
    status: 'active',
    notes: ''
  });

  const handleSave = async () => {
    try {
      // Convert billing_emails string to array
      const emailsArray = formData.billing_emails.split(',').map(e => e.trim()).filter(e => e);
      
      const response = await crmFetch(`${backendUrl}/api/crm/invoicing/programs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          billing_emails: emailsArray,
          rate: parseFloat(formData.rate)
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Program added successfully!');
        onSave();
      }
    } catch (error) {
      alert('Error adding program: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Add New Program</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program ID (URL-friendly)</label>
              <input
                type="text"
                value={formData.program_id}
                onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
                placeholder="e.g., truth-for-life-english"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program Name</label>
              <input
                type="text"
                value={formData.program_name}
                onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ministry Name</label>
              <input
                type="text"
                value={formData.ministry_name}
                onChange={(e) => setFormData({ ...formData, ministry_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
              <input
                type="text"
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                placeholder="e.g., Every Saturday at 9:00 AM"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
                <select
                  value={formData.billing_type}
                  onChange={(e) => setFormData({ ...formData, billing_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                >
                  <option value="monthly">Monthly</option>
                  <option value="hourly">Hourly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Emails (comma-separated)</label>
              <input
                type="text"
                value={formData.billing_emails}
                onChange={(e) => setFormData({ ...formData, billing_emails: e.target.value })}
                placeholder="email1@example.com, email2@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors"
            >
              Add Program
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal for Sending Invoice
const ConfirmSendModal = ({ invoice, confirmText, setConfirmText, onConfirm, onClose }) => {
  if (!invoice) return null;
  
  const isConfirmValid = confirmText === 'CONFIRM';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Invoice Send</h2>
          <p className="text-gray-600">
            You are about to send an email with this invoice to external recipients.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="space-y-2 text-sm">
            <p><span className="font-semibold">Invoice:</span> {invoice.invoice_number}</p>
            <p><span className="font-semibold">Program:</span> {invoice.program_name}</p>
            <p><span className="font-semibold">Amount:</span> ${invoice.total?.toFixed(2) || '0.00'}</p>
            <p><span className="font-semibold">Recipients:</span></p>
            <ul className="list-disc list-inside text-gray-600 ml-2">
              {(invoice.billing_emails || []).map((email, idx) => (
                <li key={idx}>{email}</li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="font-bold text-red-600">CONFIRM</span> to send this invoice:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type CONFIRM here"
            className={`w-full px-4 py-3 border-2 rounded-lg text-center text-lg font-mono uppercase tracking-wider ${
              confirmText === 'CONFIRM' 
                ? 'border-green-500 bg-green-50' 
                : confirmText.length > 0 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300'
            }`}
            autoFocus
          />
          {confirmText.length > 0 && confirmText !== 'CONFIRM' && (
            <p className="text-red-500 text-sm mt-1">Please type CONFIRM exactly (case-sensitive)</p>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmValid}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
              isConfirmValid
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            📤 Send Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

// Program Edit Modal Component (for editing existing ministries)
const ProgramEditModal = ({ program, onClose, onSave, backendUrl }) => {
  const [formData, setFormData] = useState({
    program_name: program.program_name || '',
    ministry_name: program.ministry_name || '',
    schedule: program.schedule || '',
    rate: program.rate || 0,
    billing_emails: (program.billing_emails || []).join(', '),
    status: program.status || 'active',
    notes: program.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const emailsArray = formData.billing_emails.split(',').map((e) => e.trim()).filter(Boolean);
      const r = await crmFetch(`${backendUrl}/api/crm/invoicing/programs/${program.program_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_name: formData.program_name,
          ministry_name: formData.ministry_name,
          schedule: formData.schedule,
          rate: parseFloat(formData.rate),
          billing_emails: emailsArray,
          status: formData.status,
          notes: formData.notes,
        }),
      });
      const d = await r.json();
      if (d.success) {
        alert('Ministry updated');
        onSave();
      } else {
        alert(d.detail || 'Update failed');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Edit Ministry: {program.program_id}</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program name</label>
              <input type="text" value={formData.program_name}
                onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                data-testid="program-edit-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ministry name</label>
              <input type="text" value={formData.ministry_name}
                onChange={(e) => setFormData({ ...formData, ministry_name: e.target.value })}
                data-testid="program-edit-ministry"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
              <input type="text" value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                placeholder="e.g., Every Saturday at 9:00 AM"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly rate (USD)</label>
              <input type="number" step="0.01" value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                data-testid="program-edit-rate"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing emails (comma-separated)</label>
              <input type="text" value={formData.billing_emails}
                onChange={(e) => setFormData({ ...formData, billing_emails: e.target.value })}
                data-testid="program-edit-emails"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                data-testid="program-edit-status"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              data-testid="program-edit-save-btn"
              className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceManagement;
